import { playersAbove } from "../document/band.ts";
import { COLLISION_CELLS_PER_TILE, COMPILED_COLLISION_CELLS_PER_TILE, type JsonValue, type MapDocument, type MapPlace, type Seam, type WorldDocument } from "../document/types.ts";
import { sortedRecord } from "../document/validate.ts";
import type { ValidationIssue } from "../editor/adapter.ts";
import { contextFrameSize, lowerCellSources } from "../editor/cell-sources.ts";
import { mapLinkIssues } from "../editor/door-links.ts";
import { buildAppearances } from "./behaviour.ts";
import { chunkMap } from "./chunk.ts";
import { doorIndexOf, type DoorIndex } from "./doors.ts";
import { checkMapPlace, mapIsIndoor } from "./places.ts";
import type { CompileContext, CompileWarning } from "./context.ts";
import type { ManifestObjectAppearance } from "./manifest.ts";
import { resolveMap } from "./resolve-map.ts";
import { serialiseChunk } from "./serialise-chunk.ts";

/**
 * Compiles a world document into client and server chunk texts (Playbook 5.8, ADR-016). Pure: the result is
 * a function of the document and the context, so recompiling unchanged input yields identical text. Naming,
 * hashing and compression belong to the tool layer. A prop carrying a behaviour (ADR-053) is lowered into a
 * server-chunk entity indexing the world's appearance table and leaves the client's prop layer. A cells field
 * that follows a zone (ADR-021's amendment of 2026-10-02) is lowered to the cells it resolves to first, so a
 * chunk only ever carries plain pairs.
 */

/**
 * One chunk's serialised client and server texts.
 */
export interface CompiledChunkText {
  readonly cx: number;
  readonly cy: number;
  readonly client: string;
  readonly server: string;
}

/**
 * One compiled map. `place`, `group` and `blank` carry the layout into the manifest (ADR-109 decision 11): the map's
 * place on the world grid, its map group's id and name, and `blank` for a map whose compile wrote no chunk because it
 * holds no layer or, in a compile made for a publish, is a draft. All three are absent on a map that has none.
 */
export interface CompiledMap {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly playersAbove: number;
  readonly properties?: { readonly [key: string]: JsonValue };
  readonly place?: MapPlace;
  readonly group?: { readonly id: string; readonly name: string };
  readonly blank?: true;
  readonly indoor?: true;
  readonly dim?: number;
  readonly chunks: readonly CompiledChunkText[];
}

/**
 * What a compile is made for (ADR-109 decision 9). `publish` leaves a draft map out as a blank one, so nothing of a
 * half-painted map is written, read or drawn; absent or false, the compile is the development game's and the
 * play-test's, which compile drafts in full.
 */
export interface CompileOptions {
  readonly publish?: boolean;
}

/**
 * Whether a map compiles to its entry alone: it holds no layer, or the compile is a publish and it is a draft.
 *
 * @param map The map as authored.
 * @param options The compile's options.
 * @returns True for a blank map.
 */
function isBlank(map: MapDocument, options: CompileOptions): boolean {
  return map.layers.length === 0 || (options.publish === true && map.draft === true);
}

/**
 * The layout fields a compiled map carries: its place, the id and name of its map group when the world lists it, and `indoor`
 * with its `dim` (ADR-141 decision D4) for a map that lies indoors, which a client holds at one minus the dim at every hour.
 *
 * @param world The world, for its map groups.
 * @param map The map.
 * @returns The fields to spread, none for a map with neither.
 */
function layoutOf(world: WorldDocument, map: MapDocument): { place?: MapPlace; group?: { readonly id: string; readonly name: string }; indoor?: true; dim?: number } {
  const group = map.mapGroup === undefined ? undefined : world.mapGroups?.find((g) => g.id === map.mapGroup);
  const indoor = mapIsIndoor(map);
  const dim = map.atmosphere?.dim;
  return { ...(map.place === undefined ? {} : { place: map.place }), ...(group === undefined ? {} : { group: { id: group.id, name: group.name } }), ...(indoor ? { indoor: true as const } : {}), ...(indoor && dim !== undefined && dim > 0 ? { dim } : {}) };
}

/**
 * A compiled world. `doors` is the door index the version keeps as `server/doors.json` (ADR-122 decision 2.3),
 * null when no map holds a door layer. `linkWarnings` are the broken links of draft maps (ADR-122 decision 2.5),
 * which a ready map's would refuse; the one-way door's info is in neither. `seams` are the world's seams as stored, which
 * the manifest carries so a map server knows which edges may be crossed (ADR-147); absent when the world has none.
 * `atmosphereWarnings` name a map whose `dim` does nothing because it follows the sky (ADR-141 decision D4); absent when there are none.
 */
export interface CompiledWorld {
  readonly worldId: string;
  readonly schemaVersion: number;
  readonly tileSize: number;
  readonly chunkSize: number;
  readonly maps: readonly CompiledMap[];
  readonly objectAppearances: readonly ManifestObjectAppearance[];
  readonly warnings: readonly CompileWarning[];
  readonly sightWarnings: readonly string[];
  readonly atmosphereWarnings?: readonly string[];
  readonly linkWarnings: readonly ValidationIssue[];
  readonly doors: DoorIndex | null;
  readonly seams?: readonly Seam[];
}

/**
 * The broken links of one map, read against its compiled collision (ADR-122 decision 2.5): thrown for a ready map,
 * returned for a draft.
 *
 * @param world The world.
 * @param map The map as authored.
 * @param collision Its compiled collision grid, at the compiled cell; an authoring cell is blocked when any of its
 *   compiled cells is.
 * @param context The compile context, for the arrival type.
 * @returns The draft's link warnings, empty for a ready map.
 * @throws Error naming the map and the first broken link of a ready map.
 */
function linkWarningsOf(world: WorldDocument, map: MapDocument, collision: Uint8Array, context: CompileContext): ValidationIssue[] {
  const width = map.width * COMPILED_COLLISION_CELLS_PER_TILE;
  const per = COMPILED_COLLISION_CELLS_PER_TILE / COLLISION_CELLS_PER_TILE;
  const blocked = (column: number, row: number): boolean => {
    for (let dy = 0; dy < per; dy += 1) {
      for (let dx = 0; dx < per; dx += 1) {
        if ((collision[(row * per + dy) * width + column * per + dx] ?? 0) !== 0) {
          return true;
        }
      }
    }
    return false;
  };
  const issues = mapLinkIssues(world, map, { ...(context.arrivalType === undefined ? {} : { arrivalType: context.arrivalType }), blocked: (_map, column, row) => blocked(column, row) }).filter((issue) => issue.severity !== "info");
  const first = issues[0];
  if (map.draft !== true && first !== undefined) {
    throw new Error(`map ${map.id}: ${first.message}`);
  }
  return map.draft === true ? issues : [];
}

/**
 * The Players band over the compiled layer list (ADR-047): the document band counted over the layers a
 * chunk keeps, since collision, zone and door layers fold into their grids and drop out of the list.
 *
 * @param map The map.
 * @returns Number of compiled ground layers.
 */
function compiledPlayersAbove(map: MapDocument): number {
  const band = playersAbove(map);
  return map.layers.filter((layer, index) => index < band && layer.kind !== "collision" && layer.kind !== "zone" && layer.kind !== "door").length;
}

/**
 * A map's properties as its compiled map carries them (ADR-052's amendment of 2026-09-26): key-sorted, and
 * absent for a map holding none, so such a map compiles as it did before maps carried them.
 *
 * @param map The map.
 * @returns The `properties` field to spread, or nothing.
 */
function mapProperties(map: MapDocument): { properties?: { readonly [key: string]: JsonValue } } {
  return Object.keys(map.properties).length === 0 ? {} : { properties: sortedRecord(map.properties, `maps.${map.id}.properties`) };
}

/**
 * Compiles every map of a world.
 *
 * @param world World document.
 * @param context Compile context.
 * @param options What the compile is made for; a publish writes no chunk for a draft map (ADR-109 decision 9). A map
 *   with no layer writes none in any compile (decision 11).
 * @returns Chunk texts per map plus resolver warnings and draft maps' link warnings across all maps.
 * @throws Error when a map references a terrain, tileset, rule set or entity type the context lacks, names
 *   music or ambience the game does not list, or is a ready map with a broken link: a room's host, a door's
 *   destination, or an arrival point on a door or a blocked cell (ADR-122).
 */
export function compileWorld(world: WorldDocument, context: CompileContext, options: CompileOptions = {}): CompiledWorld {
  const warnings: CompileWarning[] = [];
  const linkWarnings: ValidationIssue[] = [];
  const sightWarnings: string[] = [];
  const atmosphereWarnings: string[] = [];
  const appearances = buildAppearances(world, context, (map) => !isBlank(map, options));
  const maps = world.maps.map((authored) => {
    checkMapPlace(world, authored, context);
    if (authored.atmosphere?.dim !== undefined && !mapIsIndoor(authored)) {
      atmosphereWarnings.push(`map ${authored.id}: atmosphere.dim ${authored.atmosphere.dim} does nothing on a map that follows the sky`);
    }
    if (isBlank(authored, options)) {
      return { id: authored.id, width: authored.width, height: authored.height, playersAbove: compiledPlayersAbove(authored), ...mapProperties(authored), ...layoutOf(world, authored), blank: true as const, chunks: [] };
    }
    const map = lowerCellSources(authored, world.tileSize, contextFrameSize(context)).map;
    const resolved = resolveMap(world, map, context);
    warnings.push(...resolved.warnings);
    linkWarnings.push(...linkWarningsOf(world, authored, resolved.collision, context));
    const chunks = chunkMap(world, map, resolved, context, appearances, sightWarnings).map((pair) => ({ cx: pair.cx, cy: pair.cy, client: serialiseChunk(pair.client), server: serialiseChunk(pair.server) }));
    return { id: map.id, width: map.width, height: map.height, playersAbove: compiledPlayersAbove(map), ...mapProperties(map), ...layoutOf(world, authored), chunks };
  });
  return { worldId: world.id, schemaVersion: world.schemaVersion, tileSize: world.tileSize, chunkSize: world.chunkSize, maps, objectAppearances: appearances.appearances, warnings, sightWarnings, ...(atmosphereWarnings.length === 0 ? {} : { atmosphereWarnings }), linkWarnings, doors: doorIndexOf(world, context), ...(world.seams === undefined || world.seams.length === 0 ? {} : { seams: world.seams }) };
}
